import { useNavigate, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiX } from 'react-icons/fi';
import { Button } from '@/components/common/Button';
import { useEffect, useRef, useState } from 'react';
import { Video } from '@/data/mockData';
import api from '@/utils/api';
import toast from 'react-hot-toast';

const Container = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  background: #000;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
`;

const Header = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.xl};
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%);
  z-index: 10;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const CloseButton = styled(motion.button)`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  color: white;

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const VideoTitle = styled.h1`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: white;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin: 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`;

const VideoContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  width: 100%;
  height: 100%;
  padding: 0;
`;

const VideoPlayer = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ErrorMessage = styled.div`
  color: white;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
`;

export const VideoPlayerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { videoId } = useParams<{ videoId: string }>();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [, setIsFullscreen] = useState(false);
  const [video, setVideo] = useState<Video | null>(location.state?.video || null);
  const [loading, setLoading] = useState(!location.state?.video);

  // Fetch video if not passed via state
  useEffect(() => {
    let isMounted = true;
    
    const fetchVideo = async () => {
      if (!video && videoId && isMounted) {
        try {
          setLoading(true);
          const response = await api.get(`/videos/${videoId}`);
          if (isMounted) {
            setVideo(response.data.data);
          }
          
          // Increment view count
          await api.patch(`/videos/${videoId}/view`);
        } catch (error: any) {
          console.error('Error fetching video:', error);
          if (isMounted) {
            toast.error(error.response?.data?.message || 'Failed to load video');
          }
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      } else if (video && videoId && isMounted) {
        // Increment view count for video passed via state (only once)
        try {
          await api.patch(`/videos/${videoId}/view`);
        } catch (error) {
          console.error('Error incrementing view count:', error);
        }
      }
    };

    fetchVideo();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]); // Only depend on videoId, not video object

  useEffect(() => {
    // Enter fullscreen mode
    const enterFullscreen = async () => {
      try {
        if (containerRef.current) {
          if (containerRef.current.requestFullscreen) {
            await containerRef.current.requestFullscreen();
          } else if ((containerRef.current as any).webkitRequestFullscreen) {
            await (containerRef.current as any).webkitRequestFullscreen();
          } else if ((containerRef.current as any).mozRequestFullScreen) {
            await (containerRef.current as any).mozRequestFullScreen();
          } else if ((containerRef.current as any).msRequestFullscreen) {
            await (containerRef.current as any).msRequestFullscreen();
          }
          setIsFullscreen(true);
        }
      } catch (error) {
        console.log('Fullscreen request failed:', error);
      }
    };

    // Auto-play video when component mounts
    if (videoRef.current) {
      videoRef.current.play().catch(error => {
        console.log('Auto-play prevented:', error);
      });
    }

    // Enter fullscreen
    enterFullscreen();

    // Handle fullscreen change events
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(document.fullscreenElement || 
        (document as any).webkitFullscreenElement || 
        (document as any).mozFullScreenElement || 
        (document as any).msFullscreenElement);
      setIsFullscreen(isCurrentlyFullscreen);
      
      // If user exits fullscreen, navigate back
      if (!isCurrentlyFullscreen) {
        navigate('/video-library');
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      
      // Exit fullscreen on unmount
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else if ((document as any).webkitFullscreenElement) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).mozFullScreenElement) {
        (document as any).mozCancelFullScreen();
      } else if ((document as any).msFullscreenElement) {
        (document as any).msExitFullscreen();
      }
    };
  }, [navigate]);

  const handleClose = async () => {
    // Exit fullscreen first
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if ((document as any).webkitFullscreenElement) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).mozFullScreenElement) {
        await (document as any).mozCancelFullScreen();
      } else if ((document as any).msFullscreenElement) {
        await (document as any).msExitFullscreen();
      }
    } catch (error) {
      console.log('Exit fullscreen failed:', error);
    }
    navigate('/video-library');
  };

  if (!video) {
    return (
      <Container>
        <Header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <BackButton variant="outline" onClick={() => navigate('/video-library')}>
            <FiArrowLeft /> Back to Library
          </BackButton>
        </Header>
        <VideoContainer>
          <ErrorMessage>Video not found</ErrorMessage>
        </VideoContainer>
      </Container>
    );
  }

  return (
    <Container ref={containerRef}>
      <Header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <BackButton variant="outline" onClick={() => navigate('/video-library')}>
          <FiArrowLeft /> Back
        </BackButton>
        <VideoTitle>{video.title}</VideoTitle>
        <CloseButton
          onClick={handleClose}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FiX />
        </CloseButton>
      </Header>

      <VideoContainer>
        {loading ? (
          <div style={{ color: 'white', fontSize: '1.5rem' }}>Loading video...</div>
        ) : (
          <VideoPlayer
            ref={videoRef}
            controls
            controlsList="nodownload"
            src={video.url}
          >
            Your browser does not support the video tag.
          </VideoPlayer>
        )}
      </VideoContainer>
    </Container>
  );
};
